import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TagsBar.css';

export interface Tag {
  id: number;
  name: string;
  dateCreated?: string;
}

export interface TagsBarProps {
  // Tag data
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  
  // Permissions
  allowView?: boolean;    // default: true
  allowSelect?: boolean;  // default: true  
  allowCreate?: boolean;  // default: true
  allowRemove?: boolean;  // default: true
  
  // Configuration
  placeholder?: string;   // default: "Add tags..."
  maxTags?: number;      // default: unlimited
  className?: string;
  
  // State management
  onUnsavedChanges?: (hasChanges: boolean) => void;
}

const TagsBar: React.FC<TagsBarProps> = ({
  selectedTags = [],
  onTagsChange,
  allowView = true,
  allowSelect = true,
  allowCreate = true,
  allowRemove = true,
  placeholder = "Add tags...",
  maxTags,
  className = '',
  onUnsavedChanges
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const shouldMaintainFocusRef = useRef(false);
  const cursorPositionRef = useRef<number | null>(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || !allowSelect) return;
      
      // Save cursor position before state update
      if (inputRef.current) {
        cursorPositionRef.current = inputRef.current.selectionStart;
        shouldMaintainFocusRef.current = true;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`/api/tags/search?query=${encodeURIComponent(query)}&limit=10`);
        if (!response.ok) throw new Error('Search failed');
        
        const data = await response.json();
        setSearchResults(data.tags || []);
      } catch (err) {
        console.error('Tag search error:', err);
        setError('Failed to search tags');
        setSearchResults([]);
      } finally {
        setLoading(false);
        // Restore focus and cursor position after search completes
        if (shouldMaintainFocusRef.current && inputRef.current) {
          requestAnimationFrame(() => {
            if (inputRef.current) {
              inputRef.current.focus();
              if (cursorPositionRef.current !== null) {
                inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
              }
            }
          });
        }
      }
    }, 250),
    [allowSelect]
  );

  // Update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX
      });
    }
  }, []);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart ?? value.length;
    
    // Mark that we should maintain focus
    shouldMaintainFocusRef.current = true;
    cursorPositionRef.current = cursorPos;
    
    setInputValue(value);
    
    if (value.trim()) {
      debouncedSearch(value);
      const wasHidden = !showDropdown;
      setShowDropdown(true);
      // Only update dropdown position when showing dropdown for the first time
      if (wasHidden) {
        // Use requestAnimationFrame to ensure input maintains focus
        requestAnimationFrame(() => {
          updateDropdownPosition();
          if (inputRef.current && shouldMaintainFocusRef.current) {
            inputRef.current.focus();
            // Cursor position will be restored after state update via useEffect
          }
        });
      }
    } else {
      setShowDropdown(false);
      setSearchResults([]);
    }
    
    // Restore cursor position after React state update
    requestAnimationFrame(() => {
      if (inputRef.current && shouldMaintainFocusRef.current && cursorPositionRef.current !== null) {
        const pos = Math.min(cursorPositionRef.current, inputRef.current.value.length);
        inputRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  // Handle input key down
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const tagName = inputValue.trim();
      if (!tagName) return;
      
      // Check max tags limit first
      if (maxTags && selectedTags.length >= maxTags) {
        setError(`Maximum ${maxTags} tags allowed`);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return;
      }
      
      // Validate tag name format
      if (!/^[a-zA-Z0-9]+$/.test(tagName)) {
        setError('Tag name must contain only letters and numbers');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return;
      }
      
      if (tagName.length > 15) {
        setError('Tag name must be 15 characters or less');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return;
      }
      
      // First, check if tag exists in search results (database)
      const existingTagFromSearch = searchResults.find(tag => 
        tag.name.toLowerCase() === tagName.toLowerCase()
      );
      
      if (existingTagFromSearch) {
        // Tag exists in database, add it (checking for duplicates in selectedTags)
        const alreadySelected = selectedTags.find(tag => tag.id === existingTagFromSearch.id);
        if (!alreadySelected) {
          const updatedTags = [...selectedTags, existingTagFromSearch];
          onTagsChange(updatedTags);
          onUnsavedChanges?.(true);
        }
        
        setInputValue('');
        setShowDropdown(false);
        setError(null);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return;
      }
      
      // Check if tag is already in selectedTags (duplicate check)
      const existingInSelected = selectedTags.find(tag => 
        tag.name.toLowerCase() === tagName.toLowerCase()
      );
      
      if (existingInSelected) {
        // Tag already selected, just clear input
        setInputValue('');
        setShowDropdown(false);
        setError(null);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return;
      }
      
      // Tag doesn't exist in search results, try to create it
      if (!allowCreate) {
        setError('Tag creation is not allowed');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Try to create tag
        const response = await fetch('/api/tags', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: tagName })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          const errorMessage = errorData.error || 'Failed to create tag';
          
          // If tag already exists in database, search for it and add it
          if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
            try {
              const searchResponse = await fetch(`/api/tags/search?query=${encodeURIComponent(tagName)}&limit=10`);
              if (searchResponse.ok) {
                const searchData = await searchResponse.json();
                const foundTag = searchData.tags?.find((tag: Tag) => 
                  tag.name.toLowerCase() === tagName.toLowerCase()
                );
                
                if (foundTag) {
                  // Found the existing tag, add it
                  const alreadySelected = selectedTags.find(tag => tag.id === foundTag.id);
                  if (!alreadySelected) {
                    const updatedTags = [...selectedTags, foundTag];
                    onTagsChange(updatedTags);
                    onUnsavedChanges?.(true);
                  }
                  
                  setInputValue('');
                  setShowDropdown(false);
                  setError(null);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 0);
                  return;
                }
              }
            } catch (searchErr) {
              console.error('Error searching for existing tag:', searchErr);
            }
          }
          
          throw new Error(errorMessage);
        }
        
        // Tag created successfully
        const newTag = await response.json();
        
        // Add to selected tags
        const updatedTags = [...selectedTags, newTag];
        onTagsChange(updatedTags);
        onUnsavedChanges?.(true);
        
        setInputValue('');
        setShowDropdown(false);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } catch (err) {
        console.error('Tag creation error:', err);
        setError(err instanceof Error ? err.message : 'Failed to create tag');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } finally {
        setLoading(false);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setInputValue('');
      // Maintain focus on input
      inputRef.current?.focus();
    } else if (e.key === 'ArrowDown' && showDropdown && searchResults.length > 0) {
      // Allow arrow key navigation to dropdown (optional enhancement)
      e.preventDefault();
      // Focus first dropdown item if needed, but keep input focused for typing
      // For now, just prevent default to avoid losing input focus
    }
  };

  // Handle tag selection from dropdown
  const handleTagSelect = (tag: Tag) => {
    // Check if tag already exists
    const existingTag = selectedTags.find(t => t.id === tag.id);
    if (existingTag) {
      setInputValue('');
      setShowDropdown(false);
      // Maintain focus on input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return;
    }
    
    // Check max tags limit
    if (maxTags && selectedTags.length >= maxTags) {
      setError(`Maximum ${maxTags} tags allowed`);
      // Maintain focus on input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return;
    }
    
    const updatedTags = [...selectedTags, tag];
    onTagsChange(updatedTags);
    onUnsavedChanges?.(true);
    
    setInputValue('');
    setShowDropdown(false);
    // Restore focus to input after tag selection
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // Handle tag removal
  const handleTagRemove = (tagId: number) => {
    if (!allowRemove) return;
    
    const updatedTags = selectedTags.filter(tag => tag.id !== tagId);
    onTagsChange(updatedTags);
    onUnsavedChanges?.(true);
  };

  // Handle input focus
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    shouldMaintainFocusRef.current = true;
    cursorPositionRef.current = e.target.selectionStart;
    
    if (inputValue.trim()) {
      const wasHidden = !showDropdown;
      setShowDropdown(true);
      // Only update dropdown position when showing dropdown for the first time
      if (wasHidden) {
        // Use requestAnimationFrame to ensure input maintains focus
        requestAnimationFrame(() => {
          updateDropdownPosition();
        });
      }
    }
  };
  
  // Handle input blur - only clear focus flag if user intentionally blurred
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Check if focus is moving to dropdown (shouldn't happen due to preventDefault, but just in case)
    const relatedTarget = e.relatedTarget as Node;
    if (
      dropdownRef.current &&
      dropdownRef.current.contains(relatedTarget)
    ) {
      // Focus is moving to dropdown, prevent blur
      e.preventDefault();
      inputRef.current?.focus();
      return;
    }
    
    // User intentionally blurred (clicked elsewhere)
    shouldMaintainFocusRef.current = false;
    cursorPositionRef.current = null;
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // If clicking on dropdown, don't close it
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      
      // If clicking on input, maintain focus
      if (inputRef.current && inputRef.current.contains(target)) {
        shouldMaintainFocusRef.current = true;
        return;
      }
      
      // Clicking outside both input and dropdown
      setShowDropdown(false);
      shouldMaintainFocusRef.current = false;
      cursorPositionRef.current = null;
    };

    // Use mousedown to catch clicks before focus changes
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (showDropdown) {
      const handleScrollOrResize = () => {
        updateDropdownPosition();
        // Maintain focus during scroll/resize
        if (shouldMaintainFocusRef.current && inputRef.current) {
          inputRef.current.focus();
        }
      };
      window.addEventListener('scroll', handleScrollOrResize);
      window.addEventListener('resize', handleScrollOrResize);
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [showDropdown, updateDropdownPosition]);
  
  // Effect to maintain focus after search results update
  useEffect(() => {
    if (shouldMaintainFocusRef.current && inputRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (inputRef.current && shouldMaintainFocusRef.current) {
          inputRef.current.focus();
          // Restore cursor position if we have it
          if (cursorPositionRef.current !== null) {
            const pos = Math.min(cursorPositionRef.current, inputRef.current.value.length);
            inputRef.current.setSelectionRange(pos, pos);
          }
        }
      });
    }
  }, [searchResults, loading]);

  if (!allowView) {
    return null;
  }

  return (
    <div className={`tags-bar ${className}`}>
      {/* Selected Tags */}
      <div className="tags-bar-selected">
        {selectedTags.map(tag => (
          <div key={tag.id} className="tags-bar-tag">
            <span className="tags-bar-tag-name">{tag.name}</span>
            {allowRemove && (
              <button
                type="button"
                className="tags-bar-tag-remove"
                onClick={() => handleTagRemove(tag.id)}
                aria-label={`Remove ${tag.name} tag`}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Input Field */}
      {allowSelect && (
        <div className="tags-bar-input-container">
          <input
            ref={inputRef}
            type="text"
            className="tags-bar-input"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={loading}
            autoComplete="off"
          />
          {loading && <div className="tags-bar-loading">...</div>}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="tags-bar-error">
          {error}
        </div>
      )}

      {/* Dropdown Portal */}
      {showDropdown && allowSelect && (
        createPortal(
          <div
            ref={dropdownRef}
            className="tags-bar-dropdown"
            style={{
              position: 'absolute',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 1050
            }}
            onMouseDown={(e) => {
              // Prevent dropdown from stealing focus from input
              e.preventDefault();
            }}
          >
            {searchResults.length > 0 ? (
              searchResults.map(tag => (
                <div
                  key={tag.id}
                  className="tags-bar-dropdown-item"
                  tabIndex={-1}
                  onClick={() => {
                    handleTagSelect(tag);
                    // Restore focus to input after selection
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 0);
                  }}
                  onMouseDown={(e) => {
                    // Prevent focus loss when clicking dropdown item
                    e.preventDefault();
                  }}
                >
                  {tag.name}
                </div>
              ))
            ) : (
              <div className="tags-bar-dropdown-empty">
                {inputValue.trim() ? 'No tags found' : 'Start typing to search tags'}
              </div>
            )}
          </div>,
          document.body
        )
      )}
    </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default TagsBar;
